import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { hasAtLeastRole, type Role } from '@cfl/shared';

import { AppShell } from '@/components/shell/app-shell';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { AccountDisabledPage } from '@/routes/account-disabled';
import { EmployeeAssessmentPage, MyAssessmentsPage } from '@/routes/assessments';
import { DashboardPage } from '@/routes/dashboard';
import { EmployeeGoalsPage, MyGoalsPage } from '@/routes/goals';
import { AuditPage } from '@/routes/admin/audit';
import { FinalRatingsPage } from '@/routes/admin/final-ratings';
import { OrganizationPage } from '@/routes/admin/organization';
import { KraGroupsPage } from '@/routes/hr/kra-groups';
import { CyclesPage } from '@/routes/hr/cycles';
import { EmployeesPage } from '@/routes/hr/employees';
import { GoalAssignmentsPage } from '@/routes/goal-assignments';
import { PipPage } from '@/routes/hr/pip';
import { MeetingsPage } from '@/routes/meetings';
import { RecognitionPage } from '@/routes/recognition';
import { DevConnectPage } from '@/routes/dev-connect';
import { ReviewsPage } from '@/routes/reviews';
import { TeamPage } from '@/routes/team';
import { LoginPage } from '@/routes/login';
import { ForbiddenPage } from '@/routes/forbidden';
import { NotFoundPage } from '@/routes/not-found';
import { NotOnboardedPage } from '@/routes/not-onboarded';
import { useSession } from '@/lib/session';

/**
 * Analytics is the only route that draws charts, and recharts is ~40% of the
 * bundle. Lazy so the 90% of sessions that never open it do not pay for it.
 */
const AnalyticsPage = lazy(() =>
  import('@/routes/hr/analytics').then((m) => ({ default: m.AnalyticsPage })),
);

/**
 * Lazy for a different reason: no charts, but it is HR-only and most sessions never
 * open it, so its table and dialog do not belong in the entry chunk either.
 */
const CalibrationPage = lazy(() =>
  import('@/routes/hr/calibration').then((m) => ({ default: m.CalibrationPage })),
);

/**
 * Gate for every authenticated route.
 *
 * The three non-authenticated states are deliberately distinct destinations rather
 * than one "access denied": not signed in is a login problem, signed in without an
 * employee record is an HR onboarding problem, and the two need different words.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const session = useSession();

  if (session.status === 'loading') return <FullPageSpinner />;
  if (session.status === 'unauthenticated') return <Navigate to="/login" replace />;
  // Exited: credentials are valid, so /login would just loop them back here.
  if (session.status === 'disabled') return <Navigate to="/account-disabled" replace />;
  // Signed in, but with no department or no manager they cannot hold goals or be
  // reviewed. A page saying so beats a dashboard of empty states.
  if (session.user.needsOnboarding) return <Navigate to="/onboarding-pending" replace />;
  return <>{children}</>;
}

/** Route-level role gate. The API enforces this too; this only says so instead of a dead end. */
function RequireRole({ minimum, children }: { minimum: Role; children: React.ReactNode }) {
  const session = useSession();
  if (session.status !== 'authenticated') return <FullPageSpinner />;
  if (!hasAtLeastRole(session.user.role, minimum)) return <ForbiddenPage />;
  return <>{children}</>;
}

export function App() {
  const session = useSession();

  return (
    <Routes>
      <Route
        path="/login"
        element={session.status === 'authenticated' ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />
      <Route path="/onboarding-pending" element={<NotOnboardedPage />} />
      <Route path="/account-disabled" element={<AccountDisabledPage />} />

      <Route
        element={
          <RequireAuth>
            {/* Covers the lazy analytics chunk. FullPageSpinner rather than a
                blank frame, so a slow network shows the app is still there. */}
            <Suspense fallback={<FullPageSpinner />}>
              <AppShell />
            </Suspense>
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* M2 lands the real dashboard; every nav destination is routed now so the
            sidebar never leads to a 404 while the milestones fill in. */}
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/goals" element={<MyGoalsPage />} />
        <Route path="/team/:employeeId/goals" element={<EmployeeGoalsPage />} />
        <Route path="/assessments" element={<MyAssessmentsPage />} />
        <Route path="/team/:employeeId/assessment" element={<EmployeeAssessmentPage />} />
        <Route path="/recognition" element={<RecognitionPage />} />
          <Route path="/dev-connect" element={<DevConnectPage />} />

        <Route path="/team" element={<TeamPage />} />
        {/* Stage-scoped, so the sidebar count and the rows behind it agree. The two
            entries are not duplicates: someone who is HR and has reports owns both
            queues, and MANAGEMENT is its own screen at /admin/final-ratings. */}
        <Route path="/team/reviews" element={<ReviewsPage stage="MANAGER" />} />
        {/* Same page HR gets, and deliberately NOT under /hr: assigning goals to your
            own reports is a manager's job, and behind the HR role gate it was not
            theirs to do. The service scopes the rows to what the caller may see. */}
        <Route path="/team/goals" element={<GoalAssignmentsPage />} />
        {/* Same page HR gets at /hr/kra-groups. No RequireRole: authoring a KRA is
            open to anyone who is not an individual contributor, which is a fact about
            the reporting tree that RequireRole's role ladder cannot express. The page
            and the API both gate on canManageKraLibrary. */}
        <Route path="/team/kra" element={<KraGroupsPage />} />
        <Route path="/team/meetings" element={<MeetingsPage />} />

        {/* Department Head oversight is read-only and looks at the same numbers as
            HR analytics, so it is the same screen rather than a second copy that
            would drift. */}
        <Route
          path="/department"
          element={
            <RequireRole minimum="DEPT_HEAD">
              <AnalyticsPage />
            </RequireRole>
          }
        />
        {/* Moved to /team/goals so managers can reach it. Kept as a redirect because
            notification rows written before the move link here — and outside the HR gate,
            because the managers those links were sent to are exactly who that gate refuses. */}
        <Route path="/hr/goals" element={<Navigate to="/team/goals" replace />} />
        <Route
          path="/hr/*"
          element={
            <RequireRole minimum="HR">
              <Routes>
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="cycles" element={<CyclesPage />} />
                <Route path="kra-groups" element={<KraGroupsPage />} />
                <Route path="reviews" element={<ReviewsPage stage="HR" />} />
                <Route path="calibration" element={<CalibrationPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="pip" element={<PipPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </RequireRole>
          }
        />

        <Route
          path="/admin/*"
          element={
            <RequireRole minimum="SUPER_ADMIN">
              <Routes>
                <Route path="final-ratings" element={<FinalRatingsPage />} />
                <Route path="organization" element={<OrganizationPage />} />
                <Route path="audit" element={<AuditPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </RequireRole>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
